import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useSelectedDetailQuery } from '../../../../../../Components/QueryComponents/SelectedDetailQuery/useSelectedDetailQuery';
import {
  IBusinessCollectionProps,
  IObjektVy,
} from '../../../../../../Types/ObjektVy';
import { AssociationList } from '../../../../_Common/Components/BusinessCollectionComponents/AssociationList/AssociationList';
import { collectionCompToObjektVy } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessCollectionUtils';
import BusinessDataForm, {
  BusinessDataFormContext,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  defaultEditMenuCancelButton,
  IEditMenuAction,
  IFormData,
  IValidateFn,
  removeIdsFromNewAssociations,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import ObjektVy from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVy/ObjektVy';
import { ObjektVyKort } from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import ObjektVySida from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import { BokforderMessages } from '../Components/BokforderMessages/BokforderMessages';
import query from '../Queries/BokforderQuery.gql';
import {
  BokforderQuery,
  BokforderQueryVariables,
  BokforderQuery_node_TEDEKBokforder,
} from '../Queries/_QueryTypes/BokforderQuery';
import UpdateMutation from '../Mutations/BokforderMutation.gql';
import {
  BokforderUpdateMutation,
  BokforderUpdateMutationVariables,
} from '../Mutations/_QueryTypes/BokforderUpdateMutation';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { BokforderKontering } from '../Components/BokforderKontering/BokforderKontering';
import ObjektVyKolumner, {
  ObjektVyKolumn,
} from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKolumner/ObjektVyKolumner';
import BokforderContextManager, {
  DefaultBfoVariantEnum,
} from '../Components/BokforderContext/BokforderContextManager';
import bokforderSettingsQuery from '../Queries/BokforderSettingsQuery/BokforderSettingsQuery.gql';
import AddMutation from '../Mutations/BokforderAddMutation.gql';
import {
  BokforderAddMutation,
  BokforderAddMutationVariables,
} from '../Mutations/_QueryTypes/BokforderAddMutation';
import {
  TEDEKBokforderTYPEnum,
  TEDEKBokforderVariantEnum,
  TEDInputEKBokforderAddValues,
} from '../../../../../../globalQueryTypes';
import { handleGQLError } from '../../../../../../Utils/Error';
import {
  BokforderSettingsQuery,
  BokforderSettingsQueryVariables,
} from '../Queries/BokforderSettingsQuery/_QueryTypes/BokforderSettingsQuery';
import AddBokforderMessageButton from '../Components/AddBokforderMessageButton/AddBokforderMessageButton';
import BokforderCreateDialog from '../Components/BokforderCreateDialog/BokforderCreateDialog';
import { BokforderDetailInfoCard } from '../Components/BokforderDetailInfoCard/BokforderDetailInfoCard';
import { BokforderAreYouSureDialogWrapper } from '../Components/BokforderAreYouSureDialog/BokforderAreYouSureDialogWrapper';
import useValidateBokforder from '../Hooks/useValidateBokforder';
import {
  bfoHasPeriodRows,
  isPeriodiseringDisabled,
} from '../Utils/BokforderUtils';
import BokforderAreYouSureDialog from '../Components/BokforderAreYouSureDialog/BokforderAreYouSureDialog';
import BokforderSendBackDialog from '../Components/BokforderSendBackDialog/BokforderSendBackDialog';
import useValidateBokKontering from '../Hooks/useValidateBokfKontering';
import useValidateBokfar from '../Hooks/useValidateBokfar';
import BokforderPeriodDialog from '../Components/BokforderPeriodDialog/BokforderPeriodDialog';
import PeriodDialogWrapper from '../Components/BokforderPeriodDialog/Components/PeriodDialogWrapper';
import {
  bokforderActionTransformer,
  bokforderMessageModel,
  bokforderModel,
  getBokforderDokConfig,
} from '../Utils/detaljvyUtils';
import { useBokforderEditMenuSaveButton } from '../Hooks/useBokforderEditMenuSaveButton';
import { useBokforderEditMenuAttestButton } from '../Hooks/useBokforderEditMenuAttestButton';
import { useBokforderEditMenuSendBackButton } from '../Hooks/useBokforderEditMenuSendBackButton';
import { useBokforderEditMenuRetakeButton } from '../Hooks/useBokforderEditMenuRetakeButton';
import { useBokforderEditMenuSendButton } from '../Hooks/useBokforderEditMenuSendButton';
import useValidateKontering from '../Hooks/useValidateKontering';
import KonteringContext from '../../../../_Common/Components/KonteringManager/KonteringContext';
import EDAvenyProgress from '../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import { selectedId } from '../../../../../../Components/MasterDetail/MasterDetailUtils';
import { BokforderGridCard } from '../Components/BokforderGrid/Components/BokforderGridCard';
import { useBokforderEditMenuSendAndAttesteraButton } from '../Hooks/useBokforderEditMenuSendAndAttesteraButton';
import AdditionalAttestDialog from '../Components/AdditionalAttestDialog/AdditionalAttestDialog';
import BokforderContext from '../Components/BokforderContext/BokforderContext';
import { BokforderWrapperContext } from '../../../Rutiner/Bokforder/Contexts/BokforderWrapperContext';

export interface IBokfOrderCustomValues {
  lastUpdatedBokfOrderRad?: string;
}

export interface IBokforderUtanordningDetaljVyCommonProps {
  editMessageID: string;
  setEditMessageID: React.Dispatch<React.SetStateAction<string>>;
  type: TEDEKBokforderTYPEnum;
  variant: TEDEKBokforderVariantEnum | DefaultBfoVariantEnum | null | undefined;
}

interface IProps
  extends IBusinessCollectionProps,
    IBokforderUtanordningDetaljVyCommonProps {}

export const BokforderDetaljvy: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { activeDatabase, userID } = useContext(ActiveDatabaseContext);
  const konteringContext = useContext(KonteringContext);
  const bokforderWrapperContext = useContext(BokforderWrapperContext);

  const [periodDialogOpen, setPeriodDialogOpen] = useState(false);
  const [removeRowsDialog, setRemoveRowsDialog] = useState(false);
  const [sendBackDialogOpen, setSendBackDialogOpen] = useState(false);

  const [onSendCallback, setOnSendCallback] = useState<{
    callback: (args?: any) => void;
  }>({ callback: () => {} });

  const [removeDialogWarningText, setRemoveDialogWarningText] = useState('');

  const bokforderSettingsQueryResult = useQuery<
    BokforderSettingsQuery,
    BokforderSettingsQueryVariables
  >(bokforderSettingsQuery, {
    variables: { dbName: activeDatabase ?? '' },
  });

  const validateBokfar = useValidateBokfar();
  const validateBokfKontering = useValidateBokKontering(
    bokforderSettingsQueryResult
  );
  const validateKontering = useValidateKontering();
  const validateBokforder = useValidateBokforder(props.id);

  const { loading, nodeData, refetch } = useSelectedDetailQuery<
    BokforderQuery,
    BokforderQuery_node_TEDEKBokforder,
    BokforderQueryVariables
  >(query, props.id, { queryOptions: { onError: handleGQLError } });

  const [addMutation, { loading: addLoading }] = useMutation<
    BokforderAddMutation,
    BokforderAddMutationVariables
  >(AddMutation);

  const [updateMutation, { loading: mutationLoading, error }] = useMutation<
    BokforderUpdateMutation,
    BokforderUpdateMutationVariables
  >(UpdateMutation);

  const addFunction = useCallback(
    (values: IFormData) => {
      return addMutation({
        variables: {
          db: activeDatabase || '',
          values: values as TEDInputEKBokforderAddValues,
        },
      });
    },
    [addMutation, activeDatabase]
  );

  const updateFunction = useCallback(
    (values: IFormData) => {
      const newValues = removeIdsFromNewAssociations(values ?? {});
      return updateMutation({
        variables: {
          db: activeDatabase || '',
          values: {
            id: nodeData?.id ?? '',
            _version: nodeData?._version ?? 1,
            ...newValues,
          },
        },
      });
    },
    [updateMutation, activeDatabase, nodeData?.id, nodeData?._version]
  );

  const messageVyer = useMemo(() => {
    return [BokforderMessages];
  }, []);

  const BokforderDetailInfoCardVyer = useMemo(() => {
    return [BokforderDetailInfoCard, BokforderKontering];
  }, []);

  const bokforderStatus = useMemo(() => {
    return nodeData?.STATUS;
  }, [nodeData]);

  const queriesLoading = useMemo(
    () =>
      loading ||
      mutationLoading ||
      addLoading ||
      konteringContext.konteringValidationQueryLoading,
    [
      addLoading,
      konteringContext.konteringValidationQueryLoading,
      loading,
      mutationLoading,
    ]
  );

  const isPeriodiseraButtonDisabled = useMemo(() => {
    if (nodeData?._Associations?.RefBokfver?.VERKAT_KOD != undefined) {
      return isPeriodiseringDisabled(
        nodeData?._Associations?.RefBokfver?.VERKAT_KOD ?? ''
      );
    } else {
      return false;
    }
  }, [nodeData?._Associations?.RefBokfver?.VERKAT_KOD]);

  const editAttestButton: IEditMenuAction = useBokforderEditMenuAttestButton(
    queriesLoading,
    userID
  );

  const editMenuCancelButton: IEditMenuAction = {
    ...defaultEditMenuCancelButton,
    label: () => (props.isNew ? 'Avbryt' : 'Ångra ändringar'),
    shortLabel: () => (props.isNew ? 'Avbryt' : 'Ångra'),
    onClick: ({ businessDataFormContext, editMenuOnCancel }) => {
      props.setEditMessageID('');
      if (props.isNew) {
        businessDataFormContext?.formStateDispatch({ type: 'CANCEL' });
      } else {
        businessDataFormContext?.formStateDispatch({ type: 'RESET' });
      }

      editMenuOnCancel?.();
    },
    isDisabled: ({ businessDataFormContext }) =>
      props.isNew
        ? businessDataFormContext.formState.loading ||
          businessDataFormContext.mutationLoading
        : businessDataFormContext.mutationLoading ||
          !businessDataFormContext.formState.formHasChanged,
  };

  const editSaveButton = useBokforderEditMenuSaveButton(
    bokforderSettingsQueryResult.data?.me?.[0]?.bokfgrupper?.[0]?.id,
    bokforderStatus,
    props.isNew,
    props.type,
    queriesLoading,
    userID,
    props.setEditMessageID,
    null
  );

  const editSendBackButton: IEditMenuAction =
    useBokforderEditMenuSendBackButton(
      queriesLoading,
      userID,
      setSendBackDialogOpen
    );

  const editRetakeButton: IEditMenuAction = useBokforderEditMenuRetakeButton(
    queriesLoading,
    userID,
    props.setEditMessageID
  );

  const editSendButton = useBokforderEditMenuSendButton(
    bokforderSettingsQueryResult.data?.me?.[0]?.bokfgrupper?.[0]?.id,
    props.isNew,
    queriesLoading,
    userID,
    setRemoveRowsDialog,
    setOnSendCallback,
    null,
    props.type,
    setRemoveDialogWarningText
  );

  const editSendAndAttesteraButton = useBokforderEditMenuSendAndAttesteraButton(
    bokforderSettingsQueryResult.data?.me?.[0]?.bokfgrupper?.[0]?.id,
    props.isNew,
    queriesLoading,
    userID,
    setRemoveRowsDialog,
    setOnSendCallback,
    null,
    props.type,
    setRemoveDialogWarningText
  );

  const editPeriodButton: IEditMenuAction = useMemo(
    () => ({
      id: 'periodButton',
      isDisabled: params => queriesLoading || isPeriodiseraButtonDisabled,
      flat: params => {
        const hasPeriodRows = bfoHasPeriodRows(
          params.businessDataFormContext.formState.currentValues?._Associations
            ?.Rader ?? []
        );

        return !hasPeriodRows;
      },
      label: params => {
        const hasPeriodRows = bfoHasPeriodRows(
          params.businessDataFormContext.formState.currentValues?._Associations
            ?.Rader ?? []
        );

        return hasPeriodRows ? 'Visa Periodisering' : 'Periodisera';
      },
      onClick: () => setPeriodDialogOpen(true),
      icon: 'access_time',
      hideIconIfNotFloating: true,
    }),
    [queriesLoading, isPeriodiseraButtonDisabled]
  );

  return (
    <BusinessDataForm<BokforderQuery_node_TEDEKBokforder>
      addMutation={addFunction}
      data={nodeData}
      dataLoading={
        loading ||
        konteringContext.konteringValidationQueryLoading ||
        bokforderWrapperContext.allBFOUsersLoading
      }
      editMenuActions={[
        editPeriodButton,
        editMenuCancelButton,
        editSaveButton,
        editSendButton,
        editRetakeButton,
        editSendBackButton,
        editAttestButton,
        editSendAndAttesteraButton,
      ]}
      fullScreen={props.config?.fullPage ?? false}
      isNew={props.isNew}
      model={bokforderModel}
      mutationError={error}
      mutationLoading={mutationLoading}
      updateMutation={updateFunction}
      validateFns={[
        validateBokforder as IValidateFn,
        validateBokfKontering as IValidateFn,
        validateBokfar as IValidateFn,
        validateKontering as IValidateFn,
      ]}
      overrideChoiceButtons={{ save: editSaveButton }}
      refetchData={refetch}
      formStateActionTransformer={bokforderActionTransformer}
    >
      {queriesLoading && <EDAvenyProgress />}
      <BokforderContextManager
        settingsQueryResult={bokforderSettingsQueryResult}
        selectedDetail={props.id}
        editMessageID={props.editMessageID}
        setEditMessageID={props.setEditMessageID}
      >
        <ObjektVy
          nodeLoading={loading}
          editImplemented
          sideBySideWidths={[6, 6]}
        >
          <ObjektVySida
            caption="Bokföringsorder"
            collectionName={bokforderModel.CollectionName}
            sidNr={1}
          >
            <ObjektVyKolumner>
              <ObjektVyKolumn fullPage={false}>
                <ObjektVyKort
                  noPadding
                  bmod={bokforderModel}
                  caption="Detaljinfo"
                  id={props.id}
                  vyer={BokforderDetailInfoCardVyer}
                />
              </ObjektVyKolumn>
              <ObjektVyKolumn fullPage={false}>
                <BokforderGridCard id={props.id} />
              </ObjektVyKolumn>
              <ObjektVyKolumn fullPage={props.config?.fullPage ?? false}>
                <ObjektVyKort
                  bmod={bokforderMessageModel}
                  caption="Meddelanden"
                  id={props.id}
                  vyer={messageVyer}
                  button={() => <AddBokforderMessageButton />}
                />
              </ObjektVyKolumn>
              <ObjektVyKolumn fullPage={props.config?.fullPage ?? false}>
                <ObjektVyKort caption="Dokument" readOnly>
                  <BokforderContext.Consumer>
                    {bokforderContext => (
                      <BusinessDataFormContext.Consumer>
                        {formContext => (
                          <AssociationList
                            associationName={'Dokument'}
                            associationPath={'Dokument'}
                            collectionName={'EKBokforder'}
                            config={getBokforderDokConfig(
                              formContext,
                              userID,
                              bokforderContext.isReservForAttestant,
                              bokforderContext.isReservForKontrollattestant
                            )}
                            ownId={selectedId(props.id)}
                          />
                        )}
                      </BusinessDataFormContext.Consumer>
                    )}
                  </BokforderContext.Consumer>
                </ObjektVyKort>
              </ObjektVyKolumn>
            </ObjektVyKolumner>
            <BokforderCreateDialog />
            <BokforderAreYouSureDialogWrapper />
            <BokforderAreYouSureDialog
              boldWarningText="Ofullständiga rader tas bort från bokföringsordern. "
              open={removeRowsDialog}
              setOpen={setRemoveRowsDialog}
              title="Är du säker?"
              warningText={removeDialogWarningText}
              yesCallback={() => {
                onSendCallback.callback?.();
              }}
            />
            <BokforderSendBackDialog
              open={sendBackDialogOpen}
              setOpen={setSendBackDialogOpen}
              type={props.type}
            />
            <PeriodDialogWrapper>
              <BokforderPeriodDialog
                open={periodDialogOpen}
                setOpen={setPeriodDialogOpen}
              />
            </PeriodDialogWrapper>
            <AdditionalAttestDialog type={props.type} />
          </ObjektVySida>
        </ObjektVy>
      </BokforderContextManager>
    </BusinessDataForm>
  );
};

const BokforderObjektVy = collectionCompToObjektVy(
  BokforderDetaljvy as React.FC<
    React.PropsWithChildren<IBusinessCollectionProps>
  >,
  'Bokforder'
);

export const BokforderVyer: IObjektVy[] = [BokforderObjektVy];
